import React, { useState, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ClockIcon } from '@heroicons/react/24/outline'
import { toast } from 'sonner'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { RootState } from '@/redux/store'
import {
  fetchTimerConfigurationAPIThunk,
  saveTimerConfigurationThunk,
  fetchTimerStatusThunk,
} from '@/redux/asyncThunks/timerConfiguration'

import { useGamePhase } from '@/hooks/useGamePhase'

import { GameRoomStatus, isGameRoomSessionActive } from '@/redux/types/room'
import {
  createDefaultMultiRoundConfig,
  defaultConfig,
  defaultTimerAlerts,
  TimerMode,
} from './timer-configuration/types'
import { SingleRoundConfig } from './timer-configuration/SingleRoundConfig'
import { MultiRoundConfig } from './timer-configuration/MultiRoundConfig'
import { TimerControls } from './timer-configuration/TimerControls'
import { TimerAlertTriggersPanel } from './timer-configuration/TimerAlertTriggersPanel'
import { TimerCurrentStatusSection } from './timer-configuration/TimerCurrentStatusSection'
import { TimerModeSelector } from './timer-configuration/TimerModeSelector'
import { GameplayLength } from './timer-configuration/GameplayLength'
import { useRoomControls } from '@/hooks/useRoomControls'
import { getRoomDetails } from '@/redux/asyncThunks/room'
import { CharterRoundControlPanel } from '@/features/charter/components/Professor/CharterRoundControlPanel'
import {
  fetchCharterConfig,
  saveCharterConfig,
} from '@/features/charter/redux/asyncThunks/charterThunks'
import { selectCharterConfig } from '@/features/charter/redux/selectors/charterSelectors'

interface TimerConfigurationFormProps {
  roomId: string
}

export const TimerConfigurationForm: React.FC<TimerConfigurationFormProps> = ({
  roomId,
}) => {
  const dispatch = useAppDispatch()
  const { config, status, loading, error } = useAppSelector(
    (state: RootState) => state.timerConfiguration
  )
  const { currentRoom } = useAppSelector((state: RootState) => state.room)
  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED
  const isGameInProgress = isGameRoomSessionActive(currentRoom?.status)

  const charterConfig = useAppSelector(selectCharterConfig)
  const [localConfig, setLocalConfig] = useState(defaultConfig)
  const [isLocalConfigDirty, setIsLocalConfigDirty] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<string>('configuration')
  const [selectedRound, setSelectedRound] = useState<number>(0)
  // Round 0 (Pre-Game Charter) — local form state + which round tab is active.
  const [charterForm, setCharterForm] = useState<{
    enabled: boolean
    charter_gate_enabled: boolean
  }>({ enabled: false, charter_gate_enabled: false })
  const [charterTabSelected, setCharterTabSelected] = useState<boolean>(false)
  const canEditConfig = currentRoom?.status === GameRoomStatus.NOT_STARTED
  const {
    phaseId,
    roundId,
    phaseDuration,
    phaseStatus,
    isInfinitePhase,
    endTime,
    roundStatus,
    canGoPreviousPhase,
  } = useGamePhase()

  const {
    controlsLoading,
    timeControlsEnabled,
    setTimeControlsEnabled,
    handleStartNextRound,
    handleEndRound,
    handleEndPhase,
    handlePreviousPhase,
    handlePauseGame,
  } = useRoomControls(roomId, phaseId, roundId, roundStatus)

  useEffect(() => {
    if (roomId) {
      dispatch(getRoomDetails(roomId))
    }
  }, [roomId])

  // Load configuration and status when component mounts
  useEffect(() => {
    if (roomId) {
      dispatch(fetchTimerConfigurationAPIThunk(roomId))
      dispatch(fetchCharterConfig({ roomId }))
    }
  }, [dispatch, roomId])

  // Seed the Round 0 charter form from the fetched charter config.
  useEffect(() => {
    if (charterConfig) {
      setCharterForm({
        enabled: charterConfig.enabled ?? false,
        charter_gate_enabled: charterConfig.charter_gate_enabled ?? false,
      })
    }
  }, [charterConfig])

  // Update local state when Redux state changes
  useEffect(() => {
    if (config) {
      if (!isLocalConfigDirty) {
        setLocalConfig({
          ...config,
          roundConfigurations: config.roundConfigurations.map((r) => ({
            ...r,
            autoReleaseEnabled: r.autoReleaseEnabled ?? false,
            autoReleaseEventIds: r.autoReleaseEventIds ?? [],
          })),
          alerts: {
            ...defaultTimerAlerts,
            ...config.alerts,
          },
        })
      }
    }
  }, [config, isLocalConfigDirty])

  // Periodically refresh timer status
  useEffect(() => {
    if (roomId && activeTab === 'timer-controls') {
      const intervalId = setInterval(() => {
        dispatch(fetchTimerStatusThunk(roomId))
      }, 5000)

      return () => clearInterval(intervalId)
    }
  }, [dispatch, roomId, activeTab])

  const handleModeChange = (mode: TimerMode) => {
    setIsLocalConfigDirty(true)
    if (mode === TimerMode.SINGLE_ROUND) {
      setLocalConfig({
        ...localConfig,
        mode,
        numberOfRounds: 1,
        roundConfigurations: [defaultConfig.roundConfigurations[0]],
      })
    } else if (mode === TimerMode.SINGLE_ROUND_ADVISOR_ONLY) {
      // Create a configuration with only advisor consultation phase
      setLocalConfig({
        ...localConfig,
        mode,
        numberOfRounds: 1,
        roundConfigurations: [
          {
            advisorConsultation: 15,
            isRandR: false,
            negotiation: 1,
            decisionSubmission: 1,
            autoReleaseEnabled: false,
            autoReleaseEventIds: [],
          },
        ],
      })
    } else if (mode === TimerMode.MANUAL_MODE) {
      setLocalConfig({
        ...localConfig,
        mode,
        numberOfRounds:
          localConfig.numberOfRounds > 1 ? localConfig.numberOfRounds : 4,
        roundConfigurations: createDefaultMultiRoundConfig(
          localConfig.numberOfRounds > 1 ? localConfig.numberOfRounds : 4
        ).map((r) => ({
          ...r,
          isRandR: true,
          advisorConsultation: 0,
          negotiation: 0,
          decisionSubmission: 0,
          randrConsultation: 0,
        })),
      })
    } else {
      setLocalConfig({
        ...localConfig,
        mode,
        numberOfRounds:
          localConfig.numberOfRounds > 1 ? localConfig.numberOfRounds : 4,
        roundConfigurations: createDefaultMultiRoundConfig(
          localConfig.numberOfRounds > 1 ? localConfig.numberOfRounds : 4
        ),
      })
    }
  }

  const handleNumberOfRoundsChange = (value: string, mode: TimerMode) => {
    const numberOfRounds = parseInt(value, 10)
    if (isNaN(numberOfRounds) || numberOfRounds < 1) return
    setIsLocalConfigDirty(true)

    let roundConfigurations = [...localConfig.roundConfigurations]

    // If increasing rounds, add new rounds
    if (numberOfRounds > roundConfigurations.length) {
      for (let i = roundConfigurations.length; i < numberOfRounds; i++) {
        if (mode === TimerMode.MANUAL_MODE) {
          roundConfigurations.push({
            randrConsultation: 0,
            advisorConsultation: 0,
            negotiation: 0,
            decisionSubmission: 0,
            isRandR: true,
            autoReleaseEnabled: false,
            autoReleaseEventIds: [],
          })
        } else {
          roundConfigurations.push({
            advisorConsultation: 15,
            negotiation: 30,
            decisionSubmission: 15,
            isRandR: false,
            autoReleaseEnabled: false,
            autoReleaseEventIds: [],
          })
        }
      }
    }
    // If decreasing rounds, remove extra rounds
    else if (numberOfRounds < roundConfigurations.length) {
      roundConfigurations = roundConfigurations.slice(0, numberOfRounds)
      if (selectedRound >= numberOfRounds) {
        setSelectedRound(numberOfRounds - 1)
      }
    }

    setLocalConfig({
      ...localConfig,
      numberOfRounds,
      roundConfigurations,
    })
  }

  const handleRoundTabChange = (roundIndex: number) => {
    setSelectedRound(roundIndex)
  }

  const updateRoundConfig = (
    index: number,
    key: keyof (typeof localConfig.roundConfigurations)[0],
    value: number | boolean | number[]
  ) => {
    setIsLocalConfigDirty(true)
    let processedValue: number | boolean | number[]

    if (typeof value === 'boolean' || Array.isArray(value)) {
      processedValue = value
    } else {
      processedValue = value < 1 ? 1 : value
    }

    const updatedConfigs = [...localConfig.roundConfigurations]
    updatedConfigs[index] = {
      ...updatedConfigs[index],
      [key]: processedValue,
    }

    setLocalConfig({
      ...localConfig,
      roundConfigurations: updatedConfigs,
    })
  }

  const applySelectedRoundToAll = () => {
    setIsLocalConfigDirty(true)
    const selectedRoundConfig = localConfig.roundConfigurations[selectedRound]
    const updatedConfigs = localConfig.roundConfigurations.map(
      (roundConfig, index) => {
        if (index === selectedRound) return roundConfig
        return {
          ...roundConfig,
          // Only copy the phases that exist in the target round
          ...(roundConfig.isRandR && selectedRoundConfig.randrConsultation
            ? { randrConsultation: selectedRoundConfig.randrConsultation }
            : {}),
          advisorConsultation: selectedRoundConfig.advisorConsultation,
          negotiation: selectedRoundConfig.negotiation,
          decisionSubmission: selectedRoundConfig.decisionSubmission,
          autoReleaseEnabled: selectedRoundConfig.autoReleaseEnabled ?? false,
          autoReleaseEventIds: selectedRoundConfig.autoReleaseEventIds ?? [],
        }
      }
    )

    setLocalConfig({
      ...localConfig,
      roundConfigurations: updatedConfigs,
    })

    toast.success(
      `Round ${selectedRound + 1} configuration applied to all rounds`
    )
  }

  // Validate configuration before saving
  const validateConfig = (): boolean => {
    if (localConfig.mode === TimerMode.MANUAL_MODE) {
      return true
    }
    for (const roundConfig of localConfig.roundConfigurations) {
      if (
        !roundConfig.advisorConsultation ||
        roundConfig.advisorConsultation < 1
      ) {
        toast.error('Advisor consultation time must be at least 1 minute')
        return false
      }

      if (localConfig.mode !== TimerMode.SINGLE_ROUND_ADVISOR_ONLY) {
        if (
          roundConfig.isRandR &&
          (!roundConfig.randrConsultation || roundConfig.randrConsultation < 1)
        ) {
          toast.error(
            'Event Analyst consultation time must be at least 1 minute'
          )
          return false
        }

        if (!roundConfig.negotiation || roundConfig.negotiation < 1) {
          toast.error('Negotiation time must be at least 1 minute')
          return false
        }

        if (
          !roundConfig.decisionSubmission ||
          roundConfig.decisionSubmission < 1
        ) {
          toast.error('Decision submission time must be at least 1 minute')
          return false
        }
      }
    }
    return true
  }

  const handleSave = () => {
    if (currentRoom?.status !== GameRoomStatus.NOT_STARTED) return
    if (!validateConfig()) return

    Promise.all([
      dispatch(saveTimerConfigurationThunk({ roomId, config: localConfig })).unwrap(),
      dispatch(saveCharterConfig({ roomId, data: charterForm })).unwrap(),
    ])
      .then(() => {
        setIsLocalConfigDirty(false)
        toast.success('Timer configuration saved successfully')
      })
      .catch((error) => {
        toast.error(`Failed to save timer configuration: ${error}`)
      })
  }

  const handleStartNextRoundWrapper = () => {
    handleStartNextRound(isGameCompleted)
  }

  const handleEndRoundWrapper = () => {
    handleEndRound(isGameCompleted)
  }

  const handleEndPhaseWrapper = () => {
    handleEndPhase(isGameCompleted)
  }

  const handlePauseGameWrapper = () => {
    handlePauseGame(isGameCompleted)
  }

  const handlePreviousPhaseWrapper = () => {
    handlePreviousPhase(isGameCompleted)
  }

  if (error) {
    return (
      <div className='p-6 text-red-700 bg-red-50 rounded-md'>
        <p>Error: {error}</p>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      <div className='flex gap-2 items-center'>
        <ClockIcon className='w-6 h-6 text-dark-blue' />
        <h2 className='text-xl font-semibold text-dark-blue'>
          Timer Configuration
        </h2>
      </div>
      <p className='text-gray-500'>
        {currentRoom?.status !== GameRoomStatus.NOT_STARTED
          ? 'The timer configuration can be viewed but not edited after the game has started or completed.'
          : 'Configure the timer settings for this game'}
      </p>

      <Tabs value={activeTab} onValueChange={setActiveTab} className='w-full'>
        <TabsList className='grid w-[400px] grid-cols-2'>
          <TabsTrigger value='configuration'>Configuration</TabsTrigger>
          <TabsTrigger value='timer-controls'>Timer Controls</TabsTrigger>
        </TabsList>

        <TabsContent value='configuration' className='space-y-6'>
          <TimerModeSelector
            mode={localConfig.mode}
            onModeChange={handleModeChange}
            disabled={
              currentRoom?.status !== GameRoomStatus.NOT_STARTED ||
              currentRoom.game_mode === TimerMode.SINGLE_ROUND_ADVISOR_ONLY
            }
            showSingleAdvisorRound={false}
          />

          {/* Enable Charter Builder (Round 0 — Pre-Game). When on, a Round 0
              tab appears in the rounds configuration below. */}
          <div className='grid grid-cols-[1fr_auto] items-center gap-4 rounded-md border bg-slate-50 p-4'>
            <div>
              <Label htmlFor='enable-charter-builder'>
                Enable Charter Builder (Round 0 — Pre-Game)
              </Label>
              <p className='mt-0.5 text-sm text-gray-500'>
                Students complete a 1-on-1 prep session followed by a group
                charter session before the game begins. A manual, no-timer
                Round 0 tab appears in the rounds configuration below.
              </p>
            </div>
            <Switch
              id='enable-charter-builder'
              checked={charterForm.enabled}
              onCheckedChange={(checked) => {
                setIsLocalConfigDirty(true)
                setCharterForm((f) => ({ ...f, enabled: checked }))
                if (!checked) setCharterTabSelected(false)
              }}
              disabled={!canEditConfig}
            />
          </div>

          {localConfig.mode === TimerMode.SINGLE_ROUND ||
          localConfig.mode === TimerMode.SINGLE_ROUND_ADVISOR_ONLY ? (
            <SingleRoundConfig
              roundConfig={localConfig.roundConfigurations[0]}
              mode={localConfig.mode}
              events={currentRoom?.events ?? []}
              onUpdateConfig={(key, value) => updateRoundConfig(0, key, value)}
              disabled={!canEditConfig}
              charterEnabled={charterForm.enabled}
              charterGate={charterForm.charter_gate_enabled}
              onCharterChange={(patch) => {
                setIsLocalConfigDirty(true)
                setCharterForm((f) => ({ ...f, ...patch }))
              }}
            />
          ) : (
            <MultiRoundConfig
              numberOfRounds={localConfig.numberOfRounds}
              roundConfigurations={localConfig.roundConfigurations}
              selectedRound={selectedRound}
              mode={localConfig.mode}
              events={currentRoom?.events ?? []}
              onNumberOfRoundsChange={handleNumberOfRoundsChange}
              onRoundTabChange={(index) => {
                setCharterTabSelected(false)
                handleRoundTabChange(index)
              }}
              onUpdateConfig={updateRoundConfig}
              onApplyToAll={applySelectedRoundToAll}
              disabled={!canEditConfig}
              charterEnabled={charterForm.enabled}
              charterGate={charterForm.charter_gate_enabled}
              onCharterChange={(patch) => {
                setIsLocalConfigDirty(true)
                setCharterForm((f) => ({ ...f, ...patch }))
              }}
              charterSelected={charterTabSelected}
              onSelectCharter={() => setCharterTabSelected(true)}
            />
          )}

          <GameplayLength config={localConfig} />

          {currentRoom?.status === GameRoomStatus.NOT_STARTED && (
            <div className='pt-4'>
              <Button
                onClick={handleSave}
                className='text-white w-fit bg-dark-blue hover:bg-dark-blue-hovered'
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Timer Configuration'}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value='timer-controls' className='space-y-6'>
          <CharterRoundControlPanel roomId={roomId} />

          {!status ? (
            <TimerControls
              status={status}
              endTime={endTime || null}
              phaseDuration={phaseDuration || null}
              phaseStatus={phaseStatus || null}
              roundStatus={roundStatus}
              mode={localConfig.mode}
              loading={loading}
              controlsLoading={controlsLoading}
              timeControlsEnabled={timeControlsEnabled}
              onTimeControlsToggle={setTimeControlsEnabled}
              onStartNextRound={handleStartNextRoundWrapper}
              onEndRound={handleEndRoundWrapper}
              onPause={handlePauseGameWrapper}
              onEndPhase={handleEndPhaseWrapper}
              onPreviousPhase={handlePreviousPhaseWrapper}
              canGoPreviousPhase={canGoPreviousPhase}
              isGameCompleted={isGameCompleted}
              isGameInProgress={isGameInProgress}
              isInfinitePhase={isInfinitePhase}
            />
          ) : (
            <>
              <div className='grid gap-6 lg:grid-cols-2 items-start'>
                <TimerCurrentStatusSection
                  status={status}
                  mode={localConfig.mode}
                  endTime={endTime || null}
                  phaseStatus={phaseStatus || null}
                  roundStatus={roundStatus}
                  isInfinitePhase={isInfinitePhase}
                />
                <TimerAlertTriggersPanel
                  alerts={localConfig.alerts ?? defaultTimerAlerts}
                  onAlertsChange={(next) => {
                    setIsLocalConfigDirty(true)
                    setLocalConfig((prev) => ({
                      ...prev,
                      alerts: next,
                    }))
                  }}
                  disabled={
                    isGameCompleted ||
                    currentRoom?.status !== GameRoomStatus.NOT_STARTED
                  }
                />
              </div>
              <TimerControls
                hideStatusSection
                status={status}
                endTime={endTime || null}
                phaseDuration={phaseDuration || null}
                phaseStatus={phaseStatus || null}
                roundStatus={roundStatus}
                mode={localConfig.mode}
                loading={loading}
                controlsLoading={controlsLoading}
                timeControlsEnabled={timeControlsEnabled}
                onTimeControlsToggle={setTimeControlsEnabled}
                onStartNextRound={handleStartNextRoundWrapper}
                onEndRound={handleEndRoundWrapper}
                onPause={handlePauseGameWrapper}
                onEndPhase={handleEndPhaseWrapper}
                onPreviousPhase={handlePreviousPhaseWrapper}
                canGoPreviousPhase={canGoPreviousPhase}
                isGameCompleted={isGameCompleted}
                isGameInProgress={isGameInProgress}
                isInfinitePhase={isInfinitePhase}
              />
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
